import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ProjectInfoForm, {
  Project_INFO_FORM_VIEW_TYPES,
} from './project-info-form/project-info-form';
import { useGetProjectsUsersList } from '../../../../utils/projects-list.utils';
import { BasicsInfoStyled } from './basic-info.style';
import GroupPlanTable from './project-plan/group-project-plans/group-project-plans';
import useProjectGroups from '../../../../hooks/useProjectGroups';
import useContactsByCompany from '../../../../hooks/useContactsByCompany';
import LinkedProjectPlansTable from './project-plan/linked-plan/linked-plan';
import { useLinkedProjects } from '../../../../hooks/useLinkedProjects';
import { useCompanyDetails } from '../../../../hooks/useCompanyDetails';
import useContactName from '../../../../hooks/useContactName';

const BasicInfo = ({ form, isGroupView, projectData, isEditMode, companyData, isGeneralUser }) => {
  const { id } = useParams();
  const { data: projectGroupsData = [] } = useProjectGroups(true);
  const { leadsList } = useGetProjectsUsersList();
  const { linkedProjects } = useLinkedProjects(id);
  const { companyDetails, companyName } = useCompanyDetails(projectData?.client);

  const { fullName: contactName } = useContactName(projectData.contact);

  // Filter clients from companyData
  const clientsList = companyData
    ? Object.entries(companyData).map(([id, name]) => ({
        label: name,
        value: id,
      }))
    : [];

  const selectedCompanyId = form.watch('client');

  const { data: contacts = [] } = useContactsByCompany(selectedCompanyId);
  // Map fetched contacts to dropdown format
  const contactsList = contacts.map((contact) => ({
    label: `${contact.firstName} ${contact.lastName}`,
    value: contact.id,
  }));

  const { isDirty } = form.formState; // Tracks if form has been modified

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isEditMode && isDirty) {
        const message = isGroupView
          ? 'Hold on! Your project group changes have not been saved.'
          : 'Hold on! Your project changes have not been saved.';
        event.returnValue = message; // Standard for most browsers
        return message; // For some older browsers
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isEditMode, isDirty, isGroupView]);

  return (
    <BasicsInfoStyled isGroupView={isGroupView}>
      <div
        className="project-form"
        style={{
          pointerEvents: isEditMode ? '' : 'none',
          opacity: isEditMode ? '1.0' : '0.7',
        }}
      >
        <ProjectInfoForm
          projectData={projectData}
          form={form}
          view={
            isGroupView
              ? Project_INFO_FORM_VIEW_TYPES.groupDetail
              : Project_INFO_FORM_VIEW_TYPES.detail
          }
          showStateAndSource={false}
          projectLeadOptions={leadsList}
          clientOptions={clientsList}
          clientContactOptions={contactsList}
          projectGroupOptions={projectGroupsData}
          isClientContactDisabled={!selectedCompanyId}
        />
      </div>

      {!isGroupView && (
        <LinkedProjectPlansTable
          projectData={projectData}
          companyDetails={companyDetails}
          contactName={contactName}
          companyName={companyName}
          isGeneralUser={isGeneralUser}
        />
      )}
      {isGroupView && (
        <GroupPlanTable
          projectData={linkedProjects}
          groupClient={projectData?.client}
          companyDetails={companyDetails}
          companyName={companyName}
          isGeneralUser={isGeneralUser}
          companyData={companyData}
        />
      )}
    </BasicsInfoStyled>
  );
};

export default BasicInfo;
